import React from 'react';
import { motion } from 'framer-motion';
import { Building2, Landmark, Stethoscope, ShoppingBag, Factory, Server, GraduationCap, Shield, CheckCircle, ArrowRight, Users, Brain, Lock } from 'lucide-react';

interface SpecializedIndustryProps {
  setCurrentPage: (page: string) => void;
}

const SpecializedIndustry: React.FC<SpecializedIndustryProps> = ({ setCurrentPage }) => {
  const industries = [
    {
      icon: Building2,
      title: "Critical Infrastructure Protection",
      description: "Advanced security measures to protect government infrastructure and critical systems.",
      features: [
        "Infrastructure monitoring",
        "Threat detection & response",
        "Access control systems",
        "Security hardening"
      ]
    },
    {
      icon: Lock,
      title: "Classified Data Security",
      description: "Comprehensive protection for sensitive government data and classified information.",
      features: [
        "Data encryption",
        "Access management",
        "Secure storage",
        "Data loss prevention"
      ]
    },
    {
      icon: Users,
      title: "Team Expertise",
      description: "Dedicated security professionals with deep industry knowledge and experience.",
      features: [
        "Certified experts",
        "Industry specialists",
        "Technical consultants",
        "Security architects"
      ]
    },
    {
      icon: Brain,
      title: "Advanced Analytics",
      description: "AI-powered security analytics and threat intelligence for proactive protection.",
      features: [
        "Threat intelligence",
        "Behavioral analysis",
        "Predictive analytics",
        "Risk assessment"
      ]
    },
    {
      icon: Building2,
      title: "Financial Services",
      description: "Comprehensive security solutions for banks, financial institutions, and insurance companies.",
      features: [
        "Regulatory compliance",
        "Fraud prevention",
        "Transaction security",
        "Data protection"
      ]
    },
    {
      icon: Stethoscope,
      title: "Healthcare",
      description: "Specialized security solutions for healthcare providers and medical facilities.",
      features: [
        "HIPAA compliance",
        "Patient data security",
        "Medical device protection",
        "Telemedicine security"
      ]
    },
    {
      icon: Landmark,
      title: "Government",
      description: "Advanced security measures for government agencies and public sector organizations.",
      features: [
        "Infrastructure protection",
        "Classified data security",
        "Compliance management",
        "Emergency response"
      ]
    },
    {
      icon: Factory,
      title: "Manufacturing",
      description: "Industrial security solutions for manufacturing and production facilities.",
      features: [
        "OT/IT security",
        "Supply chain protection",
        "Industrial controls",
        "Production security"
      ]
    },
    {
      icon: ShoppingBag,
      title: "Retail",
      description: "End-to-end security for retail businesses and e-commerce platforms.",
      features: [
        "POS security",
        "Payment protection",
        "Customer data security",
        "E-commerce security"
      ]
    },
    {
      icon: Server,
      title: "Technology",
      description: "Advanced security solutions for technology companies and digital enterprises.",
      features: [
        "Cloud security",
        "DevSecOps",
        "API protection",
        "IP security"
      ]
    },
    {
      icon: GraduationCap,
      title: "Education",
      description: "Comprehensive security solutions for educational institutions and academic environments.",
      features: [
        "Campus security",
        "Student data protection",
        "Research security",
        "E-learning protection"
      ]
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <Building2 className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Industry Solutions</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Specialized cybersecurity solutions designed for your industry's unique challenges and requirements
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {industries.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="glass-panel rounded-xl p-8 hover-card"
            >
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-semibold ml-4">{feature.title}</h3>
              </div>
              
              <p className="text-muted mb-6">{feature.description}</p>
              
              <ul className="space-y-3">
                {feature.features.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                    <span className="text-muted">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Industries Grid */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Industry-Specific Solutions</h2>
            <p className="text-lg text-muted">
              Tailored security solutions for diverse industry sectors
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <industry.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold ml-4">{industry.title}</h3>
                </div>
                
                <p className="text-muted mb-6">{industry.description}</p>

                <ul className="space-y-3">
                  {industry.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Ready to Secure Your Industry?</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Contact our industry security experts to discuss your specific needs and discover how we can help protect your organization
            </p>
            <button 
              onClick={() => setCurrentPage('get-started')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Request Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SpecializedIndustry;